import { ApiError, request } from "./client";

export interface ConnectAuthenticatedResult {
  status: "authenticated";
  /// Native responses carry tokens; browser responses keep them exclusively
  /// in HttpOnly cookies.
  token?: string;
  refresh_token?: string;
}

export interface ConnectPasswordRequiredResult {
  status: "password_required";
  action: "login" | "register";
}

export interface ConnectOAuthRedirectResult {
  status: "oauth_redirect";
  url: string;
}
export interface ConnectTwoFactorResult {
  status: "two_factor_required";
  challenge: string;
  expires_at: string;
}

export type ConnectResult =
  | ConnectAuthenticatedResult
  | ConnectPasswordRequiredResult
  | ConnectOAuthRedirectResult
  | ConnectTwoFactorResult;

export function completeTwoFactor(
  challenge: string,
  code: string,
): Promise<ConnectAuthenticatedResult> {
  return request<ConnectAuthenticatedResult>("/account/totp/challenge", {
    method: "POST",
    body: { challenge, code },
    skipRefresh: true,
    noRetry: true,
  });
}

export interface TotpStatus {
  enabled: boolean;
}
export interface TotpEnrollment {
  enrollment_id: string;
  secret: string;
  otpauth_uri: string;
}
export function getTotpStatus(): Promise<TotpStatus> {
  return request<TotpStatus>("/account/totp");
}
export function beginTotpEnrollment(
  currentCode?: string,
  password?: string,
  oauthReauthToken?: string,
): Promise<TotpEnrollment> {
  return request<TotpEnrollment>("/account/totp/enroll", {
    method: "POST",
    body: {
      current_code: currentCode,
      password,
      oauth_reauth_token: oauthReauthToken,
    },
    noRetry: true,
  });
}
export function confirmTotp(
  enrollmentId: string,
  code: string,
): Promise<{ recovery_codes: string[] }> {
  return request("/account/totp/confirm", {
    method: "POST",
    body: { enrollment_id: enrollmentId, code },
    noRetry: true,
  });
}
export function regenerateTotpRecovery(
  code: string,
): Promise<{ recovery_codes: string[] }> {
  return request("/account/totp/recovery-codes", {
    method: "POST",
    body: { code },
    noRetry: true,
  });
}
export function disableTotp(code: string): Promise<void> {
  return request("/account/totp", {
    method: "DELETE",
    body: { code },
    noRetry: true,
  });
}

export interface ConnectParams {
  source?: string;
  email?: string;
  password?: string;
  code?: string;
  state?: string;
  platform?: "web";
  code_challenge?: string;
  code_verifier?: string;
}

export interface UserInfo {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  /// Present when the account is in the soft-deletion grace window.
  /// The server omits both fields for live accounts.
  deleted_at?: string;
  scheduled_purge_at?: string;
  /// Billing entitlement (mirrors the server's `BillingService::entitlement`).
  /// `active` includes the grace window; plan/interval/expiry omit when free.
  plan?: string | null;
  interval?: string | null;
  plan_expires_at?: string;
  active?: boolean;
  /// The current period is a free trial (nothing charged yet).
  trialing?: boolean;
  /// Another period is expected after `plan_expires_at`. False for a trial and
  /// for a scheduled cancellation, so a countdown only runs on a period that
  /// genuinely ends.
  renews?: boolean;
}

export interface AccountDeletionScheduledResponse {
  deleted_at: string;
  scheduled_purge_at: string;
  grace_period_days: number;
}

export interface AuthMethod {
  source: "meridian" | "google" | "microsoft" | string;
  label: string;
  linked: boolean;
  configured: boolean;
  linked_at: string | null;
  password_change_available: boolean;
  requires_native_password_change: boolean;
  removal_available: boolean;
}
export interface BrowserSession {
  id: string;
  label: string;
  created_at: string;
  last_used_at: string;
  current: boolean;
  approximate_network_recorded: boolean;
}
export function listBrowserSessions(): Promise<BrowserSession[]> {
  return request<BrowserSession[]>("/account/sessions");
}
export function revokeBrowserSession(id: string): Promise<void> {
  return request<void>(`/account/sessions/${encodeURIComponent(id)}`, {
    method: "DELETE",
    noRetry: true,
  });
}
export function revokeAllBrowserSessions(): Promise<void> {
  return request<void>("/account/sessions", {
    method: "DELETE",
    noRetry: true,
    skipRefresh: true,
  });
}

export function connect(params: ConnectParams): Promise<ConnectResult> {
  return request<ConnectResult>("/account/connect", {
    method: "POST",
    body: { ...params, platform: params.platform ?? "web" },
    // Password / OAuth exchange is not safe to retry — a duplicate
    // request could burn through OAuth codes or re-trigger emails.
    noRetry: true,
  });
}

let refreshInFlight: Promise<ConnectAuthenticatedResult> | null = null;

export function refreshSession(): Promise<ConnectAuthenticatedResult> {
  // A burst of 401 responses in one tab must share one rotation. Across tabs,
  // the server rejects the immediate loser with 409 while the winning response
  // updates the shared cookie jar; retry once after that cookie can settle.
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = performSessionRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function performSessionRefresh(): Promise<ConnectAuthenticatedResult> {
  // Server reads the refresh JWT from the HttpOnly `meridian_refresh`
  // cookie; we don't pass it explicitly.
  const options = {
    method: "POST",
    body: {},
    // Don't recurse through the 401-refresh hook on the refresh call itself.
    skipRefresh: true,
    noRetry: true,
  } as const;
  try {
    return await request<ConnectAuthenticatedResult>(
      "/account/refresh",
      options,
    );
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 409) throw error;
    await new Promise((resolve) => setTimeout(resolve, 100));
    return request<ConnectAuthenticatedResult>("/account/refresh", options);
  }
}

export function logout(): Promise<void> {
  return request<void>("/account/logout", {
    method: "POST",
    body: {},
    skipRefresh: true,
    noRetry: true,
  });
}

export function getMe(): Promise<UserInfo> {
  return request<UserInfo>("/account/me");
}

export function getDeletionStatus(): Promise<UserInfo> {
  return request<{
    deleted_at: string;
    scheduled_purge_at: string;
    grace_period_days: number;
  }>("/account/deletion-status", { skipRefresh: true }).then((status) => ({
    id: "deletion-recovery",
    email: "",
    first_name: null,
    last_name: null,
    deleted_at: status.deleted_at,
    scheduled_purge_at: status.scheduled_purge_at,
  }));
}

export function updateMe(data: {
  first_name?: string;
  last_name?: string;
}): Promise<UserInfo> {
  return request<UserInfo>("/account/me", {
    method: "PATCH",
    body: data,
  });
}

/// Non-secret sign-in-method status. The response contains no OAuth subject,
/// token, key blob, password hash, or salt.
export function listAuthMethods(): Promise<AuthMethod[]> {
  return request<AuthMethod[]>("/account/auth-methods");
}

export interface SecurityProof {
  password?: string;
  challenge?: string;
  totp_code?: string;
  oauth_reauth_token?: string;
}

export function beginOAuthSecurityReauth(
  provider: "google" | "microsoft",
  codeChallenge: string,
): Promise<{ url: string }> {
  return request(`/account/security/reauth/${provider}`, {
    method: "POST",
    body: { code_challenge: codeChallenge },
    noRetry: true,
  });
}

export function completeOAuthSecurityReauth(
  code: string,
  state: string,
  codeVerifier: string,
): Promise<{ proof_token: string }> {
  return request(`/account/security/reauth/callback`, {
    method: "POST",
    body: { code, state, code_verifier: codeVerifier },
    noRetry: true,
  });
}

export function addPasswordMethod(
  newPassword: string,
  proof: SecurityProof,
): Promise<void> {
  return request<void>("/account/auth-methods/password", {
    method: "POST",
    body: { new_password: newPassword, ...proof },
    noRetry: true,
  });
}

export function removeAuthMethod(
  source: string,
  proof: SecurityProof,
): Promise<void> {
  return request<void>(`/account/auth-methods/${encodeURIComponent(source)}`, {
    method: "DELETE",
    body: proof,
    noRetry: true,
  });
}

export function beginOAuthMethodLink(
  provider: "google" | "microsoft",
  proof: SecurityProof,
  codeChallenge: string,
): Promise<{ url: string }> {
  return request(`/account/auth-methods/${provider}/link`, {
    method: "POST",
    body: { ...proof, code_challenge: codeChallenge },
    noRetry: true,
  });
}

export function completeOAuthMethodLink(
  code: string,
  state: string,
  codeVerifier: string,
): Promise<void> {
  return request<void>("/account/auth-methods/oauth/callback", {
    method: "POST",
    body: { code, state, code_verifier: codeVerifier },
    noRetry: true,
  });
}

export function requestEmailChange(
  newEmail: string,
  proof: SecurityProof,
): Promise<void> {
  return request<void>("/account/email-change/request", {
    method: "POST",
    body: { new_email: newEmail, ...proof },
    noRetry: true,
  });
}

export function confirmEmailChange(
  token: string,
  proof: SecurityProof,
): Promise<void> {
  return request<void>("/account/email-change/confirm", {
    method: "POST",
    body: { token, ...proof },
    noRetry: true,
  });
}

/// Verify the existing password and replace it. Success invalidates refresh
/// sessions and clears this browser's HttpOnly cookies, so callers should move
/// immediately to the signed-out state.
export function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  return request<void>("/account/password", {
    method: "POST",
    body: {
      current_password: currentPassword,
      new_password: newPassword,
    },
    skipRefresh: true,
    noRetry: true,
  });
}

/// Start a password-reset flow. The server always returns 204 — the
/// response intentionally doesn't tell us whether the email matched a
/// real account (defense against enumeration). Show the same "if your
/// email is on file, we've sent a link" message regardless.
export function requestPasswordReset(email: string): Promise<void> {
  return request<void>("/account/password-reset/request", {
    method: "POST",
    body: { email },
    // Don't recurse through the 401-refresh hook; this endpoint is
    // unauthenticated and we definitely don't want to refresh during a
    // password-recovery flow.
    skipRefresh: true,
    noRetry: true,
  });
}

/// Complete a password reset. The server returns 204 on success and 400
/// for any failure (bad token, expired token, weak password) — the
/// failure modes are deliberately collapsed so the API doesn't help an
/// attacker enumerate tokens.
export function confirmPasswordReset(
  token: string,
  newPassword: string,
): Promise<void> {
  return request<void>("/account/password-reset/confirm", {
    method: "POST",
    body: { token, new_password: newPassword },
    skipRefresh: true,
    noRetry: true,
  });
}

/// Schedule the authenticated user's account for deletion. Soft-delete
/// now, hard-delete after the grace period. The server requires the
/// user's own email in `email_confirmation` (case-insensitive) as a
/// defense against accidental clicks. Idempotent during the grace
/// window — re-calling returns the existing schedule.
export function requestAccountDeletion(
  emailConfirmation: string,
  proof?: { password?: string; challenge?: string; totp_code?: string },
): Promise<AccountDeletionScheduledResponse> {
  return request<AccountDeletionScheduledResponse>("/account/me", {
    method: "DELETE",
    body: {
      email_confirmation: emailConfirmation,
      ...proof,
    },
    // No retries — DELETE on the user is intentional and the server
    // is idempotent, but we still don't want the client to be
    // surprising about it.
    noRetry: true,
  });
}

export function beginSecurityStepUp(): Promise<{
  challenge: string;
  expires_at: string;
}> {
  return request("/account/security/step-up", {
    method: "POST",
    noRetry: true,
  });
}

/// Cancel a pending account deletion during the grace window. 409 if
/// the account isn't currently scheduled for deletion.
export function cancelAccountDeletion(): Promise<void> {
  return request<void>("/account/me/cancel-deletion", {
    method: "POST",
    body: {},
    noRetry: true,
  });
}

/// Exchange a panel auto-login handoff ticket (read from the URL fragment) for a
/// browser session. The server sets the HttpOnly cookies; the token rides in the
/// POST body, never the URL. Single-use → noRetry, and skipRefresh since the
/// caller isn't yet authenticated.
export function exchangePanelLogin(token: string): Promise<ConnectResult> {
  return request<ConnectResult>("/account/panel-login/exchange", {
    method: "POST",
    body: { token },
    skipRefresh: true,
    noRetry: true,
  });
}
